// A trench rat, modelled facing +X: a big brown rat caught mid-scurry — a low
// stretched body on tiny feet, a pointed snout, round pink ears and a long
// thin tail built from cylinder segments trailing behind. The looping "scurry"
// bobs the body fast and wiggles the tail: vermin on the move along the
// duckboards. Trench rats grew infamous — this one is deliberately outsized.
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

const FUR = "#6f5138";       // brown coat
const FUR_DARK = "#523a27";  // haunches / feet
const PINK = "#c98a7a";      // ears / tail / nose
const EYE = "#1c140e";

function ratParts(): Part[] {
  const body = (p: Part): Part => ({ ...p, name: "body" });
  const tail = (p: Part): Part => ({ ...p, name: "tail" });
  return [
    // four tiny feet, planted (static under the bobbing body)
    { geometry: new THREE.BoxGeometry(0.008, 0.008, 0.006), color: FUR_DARK, position: [0.02, 0.004, 0.014] },
    { geometry: new THREE.BoxGeometry(0.008, 0.008, 0.006), color: FUR_DARK, position: [0.02, 0.004, -0.014] },
    { geometry: new THREE.BoxGeometry(0.008, 0.008, 0.006), color: FUR_DARK, position: [-0.022, 0.004, 0.015] },
    { geometry: new THREE.BoxGeometry(0.008, 0.008, 0.006), color: FUR_DARK, position: [-0.022, 0.004, -0.015] },
    // low stretched body, haunches bunched at the rear
    body({ geometry: new THREE.IcosahedronGeometry(0.022, 0), color: FUR, position: [0, 0.02, 0], scale: [2.0, 0.85, 0.95] }),
    body({ geometry: new THREE.IcosahedronGeometry(0.017, 0), color: FUR_DARK, position: [-0.024, 0.021, 0], scale: [1.0, 1.0, 1.05] }),
    // pointed snout with a pink nose tip
    body({ geometry: new THREE.ConeGeometry(0.011, 0.03, 5), color: FUR, position: [0.052, 0.019, 0], rotation: [0, 0, -Math.PI / 2] }),
    body({ geometry: new THREE.IcosahedronGeometry(0.003, 0), color: PINK, position: [0.067, 0.019, 0] }),
    // round pink ears set against the skull, tilted outward
    body({ geometry: new THREE.CylinderGeometry(0.0065, 0.0065, 0.003, 6), color: PINK, position: [0.034, 0.033, 0.009], rotation: [1.1, 0, -0.3] }),
    body({ geometry: new THREE.CylinderGeometry(0.0065, 0.0065, 0.003, 6), color: PINK, position: [0.034, 0.033, -0.009], rotation: [-1.1, 0, -0.3] }),
    // beady eyes
    body({ geometry: new THREE.IcosahedronGeometry(0.0025, 0), color: EYE, position: [0.042, 0.028, 0.009] }),
    body({ geometry: new THREE.IcosahedronGeometry(0.0025, 0), color: EYE, position: [0.042, 0.028, -0.009] }),
    // long thin tail in three tapering segments, joined end to end and curving
    // sideways in plan as it trails down to the ground
    tail({ geometry: new THREE.CylinderGeometry(0.0028, 0.0035, 0.03, 4), color: PINK, position: [-0.0568, 0.0123, 0], rotation: [0, 0, 1.75] }),
    tail({ geometry: new THREE.CylinderGeometry(0.002, 0.0028, 0.03, 4), color: PINK, position: [-0.0856, 0.009, 0.0051], rotation: [0, 0.35, 1.62] }),
    tail({ geometry: new THREE.CylinderGeometry(0.0012, 0.002, 0.028, 4), color: PINK, position: [-0.1103, 0.0078, 0.0193], rotation: [0, 0.7, 1.6] }),
  ];
}

export const trenchRat = defineModel({
  id: "trench-rat",
  tiles: { w: 1, h: 1 },
  batchId: "wwi",
  name: "Trench Rat",
  tags: ["creature", "animal", "rat", "wwi", "vermin", "animated"],
  summary: "An outsized brown rat mid-scurry, long pink tail trailing.",
  description:
    "A big brown trench rat caught mid-scurry: low stretched body, pointed " +
    "snout, round pink ears and a long thin segmented tail. The looping " +
    "scurry bobs the body fast and wiggles the tail behind it.",
  author: "Fable 5",
  footprint: 0.07,
  parts: ratParts,
  animations: [
    {
      id: "scurry",
      label: "Scurry",
      duration: 0.7,
      loop: true,
      tracks: [
        {
          // fast low bob, twice per cycle — the scuttling gait
          target: "body",
          position: [
            { t: 0, value: [0, 0, 0] },
            { t: 0.175, value: [0, 0.004, 0], ease: "sine" },
            { t: 0.35, value: [0, 0, 0], ease: "sine" },
            { t: 0.525, value: [0, 0.004, 0], ease: "sine" },
            { t: 0.7, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          // tail whipping side to side from the rump
          target: "tail",
          pivot: [-0.042, 0.016, 0],
          rotation: [
            { t: 0, value: [0, 0.3, 0] },
            { t: 0.35, value: [0, -0.3, 0], ease: "sine" },
            { t: 0.7, value: [0, 0.3, 0], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
